import { HeartIcon } from "@heroicons/react/24/outline";
import { Dialog,DialogContent,DialogTitle } from "@mui/material";
import Prices from "components/Prices";
import { ProductAttributes } from "components/ProductsAttributes";
import { CollegeProduct } from "data/data";
import { Product } from "data/types";
import { FC,useEffect,useMemo,useState } from "react";
import { toast } from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { useAddToWaitListMutation,useGetProductsWithQueryQuery,useGetProductWithQuery,useGetVariationsWithQuery } from "store/api/productsApi";
import { useCollege } from "store/utils/hooks";
import { getVariations } from "utils/getVariations";
import { _renderLoading } from "./SectionSliderProductCard";

interface WaitlistDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const WaitlistDialog: FC<WaitlistDialogProps> = ({ isOpen, onClose }) => {
  const { id: collegeId } = useCollege();
  const [addToWaitList, {isLoading: isLoadingAddToWaitList}] = useAddToWaitListMutation()
  const [selectedProduct, setSelectedProduct] = useState<CollegeProduct | null>(null);
  const {data:completeSelectedProduct, isLoading: isLoadingProduct} = useGetProductWithQuery({
    id:selectedProduct?.id,
    toWaitList: 1,
    isOpen
  }, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  })
  const [attributes, setAttributes] = useState<{ [key: string]: string }>({});
  const {data:products, isLoading: isLoadingProducts} = useGetProductsWithQueryQuery({
    collegeId,
    toWaitList: 1
  }, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  useEffect(() => {
    if (products?.length === 0) {
      setSelectedProduct(null);
    }
  }, [products]);

  const variations = selectedProduct ? getVariations(completeSelectedProduct?.attributes) : {};
  const isSelected = Object.keys(variations).length
    ? Object.keys(attributes) &&
    Object.keys(variations).every((variation) =>
      attributes.hasOwnProperty(variation)
    )
    : true;
    const { data: productVariations, isLoading: isLoadingVariations } =
    useGetVariationsWithQuery({id:selectedProduct?.id.toString(),toWaitList:1}, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });
  const product = useMemo(() => Object.keys(variations).length
  ? productVariations?.find((it: Product) =>
      it.attributes.every(
        ({ name, terms }) => attributes[name] === terms[0].name
      )
    ) || selectedProduct
    : selectedProduct, [selectedProduct, productVariations, attributes]) ;


  const handleAddToWaitlist = async (product: CollegeProduct) => {

    try {

        const res = await addToWaitList(product.id);
        if (res.error) {
          toast.error(res.error?.data?.message || "Error al agregar a la lista de espera");
        } else {
          toast.success("Se ha suscrito a la lista de espera del producto, se te notificará cuando esté disponible", {
            duration: 5000,
          });
        }

    } catch (error) {
      console.error("Error agregando a la lista de espera:", error);
      toast.error("Error al agregar a la lista de espera");
    }
  };

  const handleProductClick = (product: CollegeProduct) => {
    setSelectedProduct(product);
    setAttributes({});
  };

  const handleAttribute = (attribute: { [key: string]: string }) => {
    setAttributes((prev) => ({
      ...prev,
      ...attribute,
    }));
  };

  const handleAddSelectedToWaitlist = () => {
    if (product) {
      handleAddToWaitlist(product);
    }
  };

  const renderProductDetails = () => {
    if (!selectedProduct) return null;

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{selectedProduct.name}</h3>
        </div>
        
        <div className="grid max-sm:grid-cols-1 grid-cols-2 gap-4">
          {/* Left Column - Image */}
          <div>
            <div className="sm:w-full w-1/2 aspect-square flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
              <img
                src={product.image}
                alt={selectedProduct.name}
                className="h-full w-full object-contain object-center"
              />
            </div>
            <p className="mt-4 text-sm text-gray-500 line-clamp-2">{product.description}</p>
          </div>

          {/* Right Column - Price and Variations */}
          <div>
            <div className="mb-4 w-fit">
              <Prices
                contentClass="py-1 px-2 md:py-1.5 md:px-3 text-lg font-semibold"
                price={product.price}
                price_html={product.price_html}
              />
            </div>

            {(isLoadingVariations || isLoadingProduct) && (
              _renderLoading()
            )}

            {Object.keys(variations).length > 0 && (
              <div className="mb-4">
                {Object.keys(variations).map((variation) => (
                  <ProductAttributes
                    key={variation}
                    title={variation}
                    attributes={variations[variation]}
                    active={attributes[variation]}
                    onClick={handleAttribute}
                  />
                ))}
              </div>
            )}
            <ButtonPrimary
              onClick={handleAddSelectedToWaitlist}
              loading={isLoadingAddToWaitList}
              disabled={isLoadingVariations || isLoadingProduct || !isSelected}
            >
              <HeartIcon className="h-5 w-5 mr-2" />
              Agregar a la lista de espera
            </ButtonPrimary>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <div className="flex justify-between items-center border-b border-gray-200">
        <DialogTitle className="flex-1">
          <b>Agregar a la lista <span className="max-sm:hidden">de espera</span></b>
        </DialogTitle>
        <ButtonClose onClick={() => {
          !!selectedProduct ? setSelectedProduct(null) : onClose()
        }} className="mr-4" />
      </div>
      
      <DialogContent>
        {isLoadingProducts ? (
          <div className="flex justify-center items-center min-h-[200px]">
            {_renderLoading()}
          </div>
        ) :
        products.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-gray-500">No hay productos disponibles</p>
          </div>
        ) :
        selectedProduct ? (
          renderProductDetails()
        ) : (
          <div>
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {products
                  .map(product => (
                    <div 
                      key={product.id} 
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="flex">
                        <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-full w-full object-contain object-center"
                          />
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{product.name}</h3>
                          <p className="mt-1 text-sm text-gray-500 line-clamp-2">{product.description}</p>
                          <div className="mt-1 w-fit">
                            <Prices
                              price={product.price}
                              price_html={product.price_html}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;